import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@common/api/authService';
import DefaultAvatar from '@common/assets/icons/defaultAvatar.svg';
import { featureService } from '@common/index';
import {
    Eye, EyeOff, LogOut, X
} from 'lucide-react';
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ProfileModal.module.scss';

interface ProfileContentProps {
  onClose: () => void;
}

const ProfileContent: React.FC<ProfileContentProps> = ({ onClose }) => {
    const {
        user, logout, profile, setProfile
    } = useAuth();

    const [name, setName] = useState(profile?.userName || user?.user_metadata?.full_name || user?.email?.split('@')[0] || '');
    const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
    const [isUpdatingName, setIsUpdatingName] = useState(false);

    const handleLogout = () => {
        logout();
        onClose();
    };

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value);
    };

    const handleNameSubmit = async () => {
        if (!name || name === profile?.userName) return;

        setIsUpdatingName(true);
        try {
            await featureService.sendUsername({ user_name: name });

            // Update profile in AuthContext provider
            if (profile) {
                setProfile({
                    ...profile,
                    userName: name
                });
            }
        } catch (error) {
            console.error('Failed to update name:', error);
        } finally {
            setIsUpdatingName(false);
        }
    };

    return (
        <div className={styles.profileContainer}>
            {/* Header Section: Avatar + Name + Logout */}
            <div className={styles.headerSection}>
                <div className={styles.avatarWrapper}>
                    <img 
                        src={user?.user_metadata?.avatar_url || user?.user_metadata?.picture || DefaultAvatar} 
                        alt="Profile" 
                        className={styles.avatarImg}
                    />
                </div>
                
                <div className={styles.nameLogoutSection}>
                    <div className={styles.nameInputWrapper}>
                        <label className={styles.label}>Name</label>
                        <input 
                            type="text" 
                            value={name} 
                            onChange={handleNameChange}
                            onBlur={handleNameSubmit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleNameSubmit();
                                    (e.target as HTMLInputElement).blur();
                                }
                            }}
                            className={styles.input}
                            disabled={isUpdatingName}
                        />
                    </div>
                    
                    <button 
                        className={styles.logoutBtn}
                        onClick={handleLogout}
                        title="Log out"
                    >
                        <LogOut size={20} />
                    </button>
                </div>
            </div>
            
            <div className={styles.detailsSection}>
                {/* Email */}
                <div>
                    <label className={styles.label}>Email</label>
                    <div className={styles.emailValue}>{user?.email}</div>
                </div>
                
                {/* Password */}
                <div className={styles.passwordSection}>
                    <label className={styles.label}>Password</label>
                    <div className={styles.passwordRow}>
                        <div className={styles.passwordDots}>••••••••••••</div>
                        <button 
                            onClick={() => setIsPasswordModalOpen(true)}
                            className={styles.updatePasswordBtn}
                        >
                            Update password
                        </button>
                    </div>
                </div>
            </div>
            
            <ChangePasswordModal 
                isOpen={isPasswordModalOpen} 
                onClose={() => setIsPasswordModalOpen(false)} 
            />
        </div>
    );
};

const ChangePasswordModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
    const [showNew, setShowNew] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    
    if (!isOpen) return null;
    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            const { error } = await supabase.auth.updateUser({password: newPassword});

            if (error) {
                throw error;
            }

            setSuccess(true);
            setTimeout(() => {
                onClose();
                setNewPassword('');
                setSuccess(false);
            }, 1500);
        } catch (err: any) {
            setError(err.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    };

    // Use createPortal to ensure the modal is on top of everything
    return createPortal(
        <div className={styles.passwordModalOverlay} onClick={onClose}>
            <div className={styles.passwordModalContent} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h3>Update password</h3>
                    <button onClick={onClose}><X size={20} /></button>
                </div>
                
                <form onSubmit={handleSubmit} className={styles.modalForm}>
                    {/* Removed Email Verification as requested */}
                     
                    <div>
                        <label className={styles.label}>New Password</label>
                        <div className={styles.passwordInputWrapper}>
                            <input 
                                type={showNew ? "text" : "password"}
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                                disabled={loading || success}
                            />
                            <button type="button" onClick={() => setShowNew(!showNew)} disabled={loading || success}>
                                {showNew ?  <Eye size={18} /> : <EyeOff size={18} />}
                            </button>
                        </div>
                        {error ? <div className={styles.errorText}>{error}</div> : null}
                        {success ? <div className={styles.successText}>Password updated successfully!</div> : null}
                    </div>
                     
                    <div className={styles.modalActions}>
                        <button type="button" onClick={onClose} className={styles.cancelBtn} disabled={loading}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.confirmBtn} disabled={loading || success}>
                            {loading ? 'Updating...' : 'Confirm'}
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
};

export default ProfileContent;
